// LegalPage — single renderer for all 8 legal pages.
//
// URL: /legal/:slug — slug must match one of LEGAL_PAGES keys
// (privacy, terms, refund, account-deletion, community-guidelines,
//  shipping-delivery, cookies, grievance).
//
// Renders the body with extremely lightweight markdown:
//   ## Heading  → <h2>
//   **bold**    → <strong>
//   - bullet    → <li>
//   blank line  → paragraph break
import { useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { LEGAL_PAGES, LEGAL_NAV } from '../content/legal';

function renderInline(text) {
  // Split on **bold** while preserving order.
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith('**') && p.endsWith('**')
      ? <strong key={i}>{p.slice(2, -2)}</strong>
      : <span key={i}>{p}</span>
  );
}

function renderBody(body) {
  const lines = body.split('\n');
  const blocks = [];
  let para = [];
  let list = null;

  const flushPara = () => {
    if (para.length) {
      blocks.push({ kind: 'p', content: para.join(' ') });
      para = [];
    }
  };
  const flushList = () => {
    if (list) { blocks.push({ kind: 'ul', items: list }); list = null; }
  };

  for (const raw of lines) {
    const line = raw.trim();
    if (line.startsWith('## ')) {
      flushPara(); flushList();
      blocks.push({ kind: 'h2', content: line.slice(3).trim() });
    } else if (line.startsWith('- ')) {
      flushPara();
      list = list || [];
      list.push(line.slice(2).trim());
    } else if (line === '') {
      flushPara(); flushList();
    } else {
      flushList();
      para.push(line);
    }
  }
  flushPara(); flushList();

  return blocks.map((b, i) => {
    if (b.kind === 'h2') {
      return <h2 key={i} className="text-lg font-extrabold text-[#2B3A67] mt-8 mb-3">{b.content}</h2>;
    }
    if (b.kind === 'ul') {
      return (
        <ul key={i} className="list-disc pl-6 space-y-1.5 mb-4 text-sm text-gray-700 leading-relaxed">
          {b.items.map((it, j) => <li key={j}>{renderInline(it)}</li>)}
        </ul>
      );
    }
    return (
      <p key={i} className="text-sm text-gray-700 leading-relaxed mb-4">
        {renderInline(b.content)}
      </p>
    );
  });
}

export default function LegalPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const page = LEGAL_PAGES[slug];

  useEffect(() => {
    if (page) document.title = `${page.title} · Backyard`;
    return () => { document.title = 'Backyard'; };
  }, [page]);

  if (!page) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB] px-6">
        <div className="text-center max-w-md">
          <p className="text-3xl font-extrabold text-[#2B3A67] mb-2">Page not found</p>
          <p className="text-sm text-gray-500 mb-6">We couldn't find the legal page you were looking for.</p>
          <button
            data-testid="legal-not-found-back"
            onClick={() => navigate('/legal/privacy')}
            className="px-5 py-2.5 rounded-2xl bg-[#E54562] text-white font-bold text-sm"
          >View Privacy Policy</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB] pb-nav" style={{ fontFamily: "'Outfit', sans-serif" }}>
      {/* Header */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-3">
          <button
            data-testid="legal-back-btn"
            onClick={() => navigate(-1)}
            className="p-2 -ml-2 rounded-full hover:bg-gray-100"
          >
            <i className="ph-bold ph-arrow-left text-xl text-[#2B3A67]" />
          </button>
          <h1 data-testid="legal-page-title" className="text-lg sm:text-xl font-extrabold text-[#2B3A67] flex-1">
            {page.title}
          </h1>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6 pt-6">
        {/* Sidebar nav */}
        <nav data-testid="legal-sidebar" className="lg:sticky lg:top-6 lg:self-start bg-white rounded-2xl p-3 border border-gray-100 shadow-sm h-fit">
          {LEGAL_NAV.map((item) => (
            <Link
              key={item.slug}
              to={`/legal/${item.slug}`}
              data-testid={`legal-nav-${item.slug}`}
              className={`block px-3 py-2 rounded-xl text-xs font-semibold ${
                item.slug === slug
                  ? 'bg-[#2B3A67] text-white'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        {/* Content */}
        <article className="bg-white rounded-2xl p-6 sm:p-8 border border-gray-100 shadow-sm">
          <p data-testid="legal-page-summary" className="text-sm text-gray-500 mb-6 italic">{page.short}</p>
          {renderBody(page.body)}
        </article>
      </div>
    </div>
  );
}
